/**
 * Auth API Service
 * 
 * Feature-based authentication API with centralized mock data
 */

import { User, LoginCredentials, RegisterCredentials } from '../../types'
import { mockUsers, getMockUserByEmail } from '../../mocks/data/users'
import { mockDelay, createMockSuccessResponse, createMockErrorResponse, MOCK_ERRORS } from '../../mocks/utils/mockApi'

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }
}

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
  }
  return response.json()
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    console.log('Real login called with:', credentials)
    
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    })
    return handleResponse(response)
  },

  async register(credentials: RegisterCredentials): Promise<{ user: User; token: string }> {
    console.log('Real register called with:', credentials)
    
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    })
    return handleResponse(response)
  },

  async logout(): Promise<void> {
    console.log('Real logout called')
    
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    await handleResponse(response)
    // return handleResponse(response)
  },

  async getProfile(): Promise<User> {
    console.log('Mock getProfile called')
    await mockDelay(500)
    
    // In real implementation, this would validate the token and return user data
    const mockUser = mockUsers[0] // Return first user for demo
    if (!mockUser) {
      throw new Error('User not found')
    }
    
    return mockUser
    
    // Real implementation (commented out for development)
    // const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    //   headers: getAuthHeaders(),
    // })
    // return handleResponse(response)
  },

  async updateProfile(userData: Partial<User>): Promise<User> {
    console.log('Mock updateProfile called with:', userData)
    await mockDelay(1000)
    
    const mockUser = { ...mockUsers[0], ...userData }
    return mockUser
    
    // Real implementation (commented out for development)
    // const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    //   method: 'PUT',
    //   headers: getAuthHeaders(),
    //   body: JSON.stringify(userData),
    // })
    // return handleResponse(response)
  },
} 